// =============================================================================
// HOOK USEGLOBALCHARTDATA - Données graphiques agrégées pour tous les chantiers
// =============================================================================

import { useMemo } from 'react';
import { ChantierRentabilite } from './useChantiersFiltresRentabilite';
import { CHART_STATUS_COLORS, getMargeColor } from '@/components/charts/chartConfig';

// =============================================================================
// TYPES
// =============================================================================

export interface EvolutionGlobalePoint {
  date: string;
  dateLabel: string;
  margeTotal: number;
  coutsTotal: number;
  revenusTotal: number;
}

export interface ComparaisonChantierItem {
  id: string;
  nom: string;
  rentabilite: number;
  marge: number;
  revenus: number;
  couts: number;
  couleur: string;
}

export interface RepartitionCoutsItem {
  name: string;
  value: number;
  color: string;
  pct: number;
}

export interface GlobalChartData {
  // Comparaison par chantier (bar chart)
  comparaisonChantiers: ComparaisonChantierItem[];

  // Répartition coûts globale (donut)
  repartitionCouts: RepartitionCoutsItem[];

  // Totaux pour affichage
  totaux: {
    revenus: number;
    couts: number;
    marge: number;
    rentabiliteMoyenne: number;
    nombreChantiers: number;
  };
}

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

export function useGlobalChartData(chantiers: ChantierRentabilite[]): GlobalChartData {
  // Comparaison par chantier (triée par rentabilité décroissante)
  const comparaisonChantiers = useMemo((): ComparaisonChantierItem[] => {
    return chantiers
      .filter(c => c.metrics) // Seulement ceux avec des métriques
      .map(c => {
        const rentabilite = c.metrics?.marge_finale_pct || 0;
        const marge = c.metrics?.marge_finale || 0;
        const couts = (c.metrics?.couts_fixes_engages || 0) + (c.metrics?.cout_main_oeuvre_reel || 0);
        const revenus = c.budget_ht || 0;

        return {
          id: c.id,
          nom: c.nom_chantier.length > 20
            ? c.nom_chantier.substring(0, 20) + '...'
            : c.nom_chantier,
          rentabilite,
          marge,
          revenus,
          couts,
          couleur: getMargeColor(rentabilite),
        };
      })
      .sort((a, b) => b.rentabilite - a.rentabilite);
  }, [chantiers]);

  // Répartition des coûts globale - seulement les données réelles
  const repartitionCouts = useMemo((): RepartitionCoutsItem[] => {
    // Agréger les coûts de tous les chantiers
    let totalMO = 0;
    let totalCoutsFixes = 0;

    chantiers.forEach(c => {
      if (c.metrics) {
        totalMO += c.metrics.cout_main_oeuvre_reel || 0;
        totalCoutsFixes += c.metrics.couts_fixes_engages || 0;
      }
    });

    const total = totalMO + totalCoutsFixes;

    if (total === 0) return [];

    // Afficher seulement les catégories avec des vraies données
    return [
      {
        name: "Main d'œuvre",
        value: totalMO,
        color: CHART_STATUS_COLORS.mainOeuvre,
        pct: (totalMO / total) * 100,
      },
      {
        name: 'Coûts fixes',
        value: totalCoutsFixes,
        color: CHART_STATUS_COLORS.materiaux,
        pct: (totalCoutsFixes / total) * 100,
      },
    ].filter(item => item.value > 0);
  }, [chantiers]);

  // Calcul des totaux
  const totaux = useMemo(() => {
    const revenus = chantiers.reduce((sum, c) => sum + (c.budget_ht || 0), 0);
    const couts = chantiers.reduce((sum, c) => {
      const m = c.metrics;
      return sum + (m ? (m.couts_fixes_engages + m.cout_main_oeuvre_reel) : 0);
    }, 0);
    const marge = chantiers.reduce((sum, c) => sum + (c.metrics?.marge_finale || 0), 0);
    const rentabiliteMoyenne = chantiers.length > 0
      ? chantiers.reduce((sum, c) => sum + (c.metrics?.marge_finale_pct || 0), 0) / chantiers.length
      : 0;

    return {
      revenus,
      couts,
      marge,
      rentabiliteMoyenne,
      nombreChantiers: chantiers.length,
    };
  }, [chantiers]);

  return {
    comparaisonChantiers,
    repartitionCouts,
    totaux,
  };
}
